// src/components/FormModals/ContactFormModal.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';

const ContactFormModal = ({ onClose, onSubmit, contactData = {} }) => {
  // Initialize form state with possible contactData for editing; otherwise empty for new contact.
  const [name, setName] = useState(contactData.name || '');
  const [email, setEmail] = useState(contactData.email || '');
  const [phone, setPhone] = useState(contactData.phone || '');
  const [contactType, setContactType] = useState(contactData.contactType || 'Individual');

  // Company logic: allow user to either choose existing company or create new.
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [createNewCompany, setCreateNewCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');

  // Office information (optional)
  const [officeName, setOfficeName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [country, setCountry] = useState('');
  const [isHQ, setIsHQ] = useState(false);

  // Fetch existing companies on mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const resp = await axios.get('/api/myteconstruction/companies', {
          // Assumes you have an endpoint to get companies for the current org
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
        });
        if (resp.data.status === 'success') {
          setCompanies(resp.data.data);
        } else {
          toast.error('Failed to fetch companies');
        }
      } catch (err) {
        console.error(err);
        toast.error('Server error fetching companies');
      }
    };

    fetchCompanies();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Build the payload for creating a contact.
    // If creating a new company, pass newCompanyName.
    // Otherwise, pass selectedCompanyId (if chosen).
    const payload = {
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      contactType,
    };

    if (createNewCompany && newCompanyName.trim()) {
      payload.companyName = newCompanyName.trim();
    } else if (selectedCompanyId) {
      // Optionally, if you store company ID in the contact record,
      // you might look up the company name from the companies list.
      const comp = companies.find(c => c._id === selectedCompanyId);
      payload.companyName = comp ? comp.companyName : '';
    }
    // Optionally include office information if provided
    if (officeName.trim()) {
      payload.officeName = officeName.trim();
      payload.address = address.trim();
      payload.city = city.trim();
      payload.stateProvince = stateProvince.trim();
      payload.country = country.trim();
      payload.isHQ = isHQ;
    }

    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded p-6 max-w-md w-full relative">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-400 hover:text-white"
          aria-label="Close"
        >
          <X />
        </button>
        <h3 className="text-xl font-bold mb-4">Add Contact</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full p-2 rounded bg-gray-700 text-white"
              required
            />
          </div>
          <div>
            <label className="block text-sm">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-2 rounded bg-gray-700 text-white"
              required
            />
          </div>
          <div>
            <label className="block text-sm">Phone</label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full p-2 rounded bg-gray-700 text-white"
              required
            />
          </div>
          <div>
            <label className="block text-sm">Contact Type</label>
            <select
              value={contactType}
              onChange={(e) => setContactType(e.target.value)}
              className="w-full p-2 rounded bg-gray-700 text-white"
            >
              <option value="Client">Client</option>
              <option value="Supplier">Supplier</option>
              <option value="Subcontractor">Subcontractor</option>
              <option value="Individual">Individual</option>
            </select>
          </div>
          <div>
            <label className="block text-sm mb-1">Company</label>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={createNewCompany}
                onChange={() => setCreateNewCompany(!createNewCompany)}
                id="createNewCompany"
              />
              <label htmlFor="createNewCompany" className="text-sm">
                Create new company
              </label>
            </div>
            {createNewCompany ? (
              <input
                type="text"
                placeholder="New Company Name"
                value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
                className="w-full p-2 rounded bg-gray-700 text-white mt-1"
              />
            ) : (
              <select
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                className="w-full p-2 rounded bg-gray-700 text-white mt-1"
              >
                <option value="">Select a company</option>
                {companies.map((company) => (
                  <option key={company._id} value={company._id}>
                    {company.companyName}
                  </option>
                ))}
              </select>
            )}
          </div>
          {/* Office details (optional) */}
          <div>
            <label className="block text-sm mb-1">Office (optional)</label>
            <input
              type="text"
              placeholder="Office Name (e.g., HQ)"
              value={officeName}
              onChange={(e) => setOfficeName(e.target.value)}
              className="w-full p-2 rounded bg-gray-700 text-white mt-1"
            />
            <input
              type="text"
              placeholder="Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full p-2 rounded bg-gray-700 text-white mt-1"
            />
            <input
              type="text"
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full p-2 rounded bg-gray-700 text-white mt-1"
            />
            <input
              type="text"
              placeholder="State/Province"
              value={stateProvince}
              onChange={(e) => setStateProvince(e.target.value)}
              className="w-full p-2 rounded bg-gray-700 text-white mt-1"
            />
            <input
              type="text"
              placeholder="Country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full p-2 rounded bg-gray-700 text-white mt-1"
            />
            <div className="mt-1">
              <input
                type="checkbox"
                checked={isHQ}
                onChange={() => setIsHQ(!isHQ)}
                id="isHQ"
              />
              <label htmlFor="isHQ" className="text-sm ml-1">
                Mark as HQ Office
              </label>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-blue-600 px-4 py-2 rounded text-white"
            >
              Save Contact
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

ContactFormModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  contactData: PropTypes.object, // Optional; if editing an existing contact
};

export default ContactFormModal;
